import { useState, useEffect } from "react";
import { RegistrarRecaudacionMasiva } from "../page/registrarRecaudacion";
import { getDetalle, validarArchivo } from "../serviceRM/serviceRM";
import { ArgumentosRecaudacionDTO, DetalleArchivoRecaudadoDto, ErrorCarga, RespuestaDividendoProcesarDTO } from "../models/registrarRecaudacionMasiva";
import { AxiosResponse } from "axios";
import { StatusCode } from "../../../../utils/enums";
import { RecaudacionMasivaApiService, IniciarRecaudacionResponse, ProgresoRecaudacionResponse } from "../services/recaudacionMasivaApiService";
import { useRecaudacionContext } from "../../../../context/recaudacionContext";
import { safeLog } from "../../../../utils/logSafe";
import { ListResponseDto } from "../../../../dtos/ListResponseDto";

export function useRegistrarRecaudacionMasiva() {
    const [data, setData] = useState<DetalleArchivoRecaudadoDto[]>([]);
    const [dataError, setDataError] = useState<ErrorCarga[]>([]);

    // Usar el contexto global para el estado de recaudación
    const {
        recaudacionId,
        recaudacionEnProceso,
        recaudacionCompletada,
        progresoActual,
        isConnected,
        setRecaudacionId,
        setRecaudacionEnProceso,
        setRecaudacionCompletada,
        setProgresoActual,
        conectarProgreso,
        desconectarProgreso,
        limpiarRecaudacion
    } = useRecaudacionContext();



    /**
     * Refresca los datos de la grilla para mostrar solo los registros en estado CARGADO
     */
    const refrescarDatos = async () => {
        try {
            console.log('🔄 Refrescando datos de la grilla...');
            
            // Buscar la entidad recaudadora actual
            let entidadRecaudadora: string | null = null;
            
            // Intentar obtener la entidad desde los datos actuales
            if (data.length > 0 && data[0]?.rutRecaudador) {
                entidadRecaudadora = data[0].rutRecaudador;
            }
            
            if (entidadRecaudadora) {
                safeLog('📋 Refrescando datos para entidad:', entidadRecaudadora);
                await getDetalleArchivoRecaudado(entidadRecaudadora);
                console.log('✅ Datos refrescados exitosamente');
            } else {
                console.log('⚠️ No se pudo determinar la entidad recaudadora para refrescar datos');
            }
        } catch (error) {
            console.error('❌ Error al refrescar datos:', error);
        }
    };

const getRegistrarRecaudacionMasiva = async (data: ArgumentosRecaudacionDTO): Promise<RespuestaDividendoProcesarDTO> => {
    const response: AxiosResponse<RespuestaDividendoProcesarDTO> = await validarArchivo(data);
    if (response.status === StatusCode.Ok) {
        if (response.data.dividendosProcesar.length) {
            // Agregar los nuevos datos a los existentes en lugar de reemplazar
            setData(response.data.dividendosProcesar);
        }

        if (response.data.erroresCarga.length)
            setDataError(response.data.erroresCarga);
        
        return response.data;
    }
    
    return {
        dividendosProcesar: [],
        erroresCarga: []
    };
};
    const getDetalleArchivoRecaudado = async (rut: string) => {
        const resp: AxiosResponse<ListResponseDto<DetalleArchivoRecaudadoDto>> = await getDetalle(rut);
        if (resp.status === StatusCode.Ok) {
            if (resp.data.items && Array.isArray(resp.data.items)) {
                setData(resp.data.items);
            } else {
                setData([]);
            }
        }
    };

    /**
     * Inicia el proceso de recaudación masiva
     * @param filasSeleccionadas - Array de filas seleccionadas para recaudar
     * @returns Promise con el resultado de la iniciación
     */
    const iniciarRecaudacionMasiva = async (filasSeleccionadas: DetalleArchivoRecaudadoDto[]): Promise<IniciarRecaudacionResponse | undefined> => {
        try {
            console.log('🔄 Iniciando recaudación masiva...');
            
            const response = await RecaudacionMasivaApiService.iniciarRecaudacion(filasSeleccionadas);

            if (response?.recaudacionId) {
                safeLog('✅ Recaudación iniciada exitosamente:', response);
                safeLog('📋 ID de recaudación asignado:', response.recaudacionId);

                await conectarProgreso(response.recaudacionId);
                
                return response;
            }            
        } catch (error) {
            console.error('❌ Error al iniciar recaudación:', error);
            throw error;
        }
    };

    /**
     * Cancela el proceso de recaudación en curso
     */
    const cancelarRecaudacion = async (): Promise<void> => {
        if (recaudacionId) {
            try {
                console.log('🛑 Cancelando recaudación:', recaudacionId);
                await RecaudacionMasivaApiService.cancelarRecaudacion(recaudacionId);
                setRecaudacionEnProceso(false);
                setRecaudacionId(null);
                console.log('✅ Recaudación cancelada exitosamente');
            } catch (error) {
                console.error('❌ Error al cancelar recaudación:', error);
                throw error;
            }
        }
    };

    /**
     * Actualiza los resultados de recaudación completada
     */
    const actualizarResultadosRecaudacion = (resultados: ProgresoRecaudacionResponse) => {
        safeLog('📋 Actualizando resultados de recaudación:', resultados);
        setRecaudacionCompletada(resultados);
    };

    /**
     * Limpia completamente el estado (para iniciar un nuevo proceso)
     */
    const limpiarCompletamente = () => {
        console.log('🧹 Limpiando estado completamente para nuevo proceso');
        limpiarRecaudacion(); // Usar la función del contexto
    };

    return {
        data,
        dataError,
        recaudacionId,
        recaudacionEnProceso,
        recaudacionCompletada,
        progresoActual,
        isConnected,
        getRegistrarRecaudacionMasiva,
        getDetalleArchivoRecaudado,
        iniciarRecaudacionMasiva,
        cancelarRecaudacion,
        limpiarRecaudacion,
        actualizarResultadosRecaudacion,
        refrescarDatos,
        limpiarCompletamente
    };
}